import React, { useContext, useEffect, useState, createContext } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import useQueryProfile from 'hooks/useQueryProfile';

import * as AuthUtils from 'utils/auth';
import * as Iterate from 'utils/iterate';
import * as Intercom from 'utils/intercom';
import * as Amplitude from 'utils/amplitude';
import * as Bugsnag from 'utils/bugsnag';
import * as Config from 'utils/config';
import { createUserFromEmployee } from 'utils/featureFlag';

import AppLoader from 'components/common/AppLoader';

import FeatureFlagContext from 'contexts/FeatureFlagContext';
import { Employee, Company } from './types';

interface ProfileContextInterface {
  company: Company;
  employee: Employee;
  refetchProfile: () => void;
}

const ProfileContext = createContext<ProfileContextInterface>({
  company: {} as Company,
  employee: {} as Employee,
  refetchProfile: () => {},
});

const renderProfileContext = ({
  profile_data,
  refetchProfile,
  children,
}: $TSFixMe) => {
  const { employee, company } = profile_data;
  return (
    <ProfileContext.Provider
      value={{
        employee,
        company,
        refetchProfile,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const ProfileContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [feature_flag_initialized, setFeatureFlagInitialized] = useState(false);
  const feature_flag = useContext(FeatureFlagContext);
  const is_logged_in = AuthUtils.isAuthCookiePresent();
  const handleError = useErrorHandler();
  const {
    data: profile_data,
    is_loading: is_profile_loading,
    refetch: refetchProfile,
  } = useQueryProfile({
    is_logged_in,
    onSuccess: ({ employee: { id } }) => AuthUtils.saveEmployeeIdCookie(id),
    onError: (error: $TSFixMe) => handleError(error),
  });

  useEffect(() => {
    if (profile_data) {
      const { employee, company } = profile_data;

      Amplitude.initAmplitude(employee);
      Amplitude.identifyUser(employee);
      Bugsnag.setUser(employee);
      Intercom.initIntercom(employee, company);
      Iterate.initIterate(employee, company);

      // @ts-expect-error ts-migrate(2339) FIXME: Property 'FS' does not exist on type 'Window & typ... Remove this comment to see the full error message
      window.FS.identify(employee.id, {
        first_name: employee.first_name,
        last_name: employee.last_name,
        email: employee.email,
        company: company.id,
        location: employee.settings.address?.locality,
        state: employee.settings.address?.state,
        zip: employee.settings.address?.zip,
      });
    }
  }, [profile_data]);

  useEffect(() => {
    const initFeatureFlag = async () => {
      if (profile_data) {
        const { employee } = profile_data;
        await feature_flag.setUser(createUserFromEmployee(employee));
        setFeatureFlagInitialized(true);
      }
    };
    initFeatureFlag();
  }, [feature_flag, profile_data]);

  if (!is_logged_in) {
    window.location.href = Config.getLogInRedirectUrl();
    return null;
  }

  const is_loading = is_profile_loading || !feature_flag_initialized;

  return (
    <AppLoader is_loading={is_loading}>
      {is_loading
        ? null
        : renderProfileContext({ profile_data, refetchProfile, children })}
    </AppLoader>
  );
};

export default ProfileContext;
